/* eslint-disable react/jsx-no-target-blank */
import React from 'react'
import {useIntl} from 'react-intl'
import {useQuery} from 'react-query'
import {SidebarMenuItemWithSub} from './SidebarMenuItemWithSub'
import {SidebarMenuItem} from './SidebarMenuItem'
import {useAuth} from '../../../../../app/modules/auth'
import {fetchPendingReviewsCount} from '../../../../../app/pages/reviews-management/reviewsManagementApi'
import {
  canSeeBlog,
  canSeeClients,
  canSeeContentSettings,
  canSeeGlobalSettings,
  canSeeHotelReservations,
  canSeeHotelsContent,
  canSeeMailOrders,
  canSeeNewsletter,
  canSeePages,
  canSeeReports,
  canSeeReviews,
  canSeeSettings,
  canSeeTourConfigurations,
  canSeeTourReservations,
  canSeeToursContent,
  canSeeUserManagement,
} from '../../../../../app/utils/roleUtils'

const SidebarMenuMain = () => {
  const intl = useIntl()
  const {auth} = useAuth()
  const role = auth?.role || localStorage.getItem('role') || undefined

  const {data: pendingReviewsCount = 0} = useQuery(
    ['tour-reviews-pending-count'],
    fetchPendingReviewsCount,
    {refetchInterval: 120_000, refetchOnWindowFocus: true},
  )

  const showHotelMenu =
    canSeeHotelsContent(role) || canSeeHotelReservations(role) || canSeeContentSettings(role)
  const showTourMenu =
    canSeeToursContent(role) || canSeeTourReservations(role) || canSeeTourConfigurations(role)
  const showSettingsMenu =
    canSeeSettings(role) || canSeeGlobalSettings(role) || canSeeTourConfigurations(role)

  return (
    <nav className='flex-1 space-y-0.5'>
      {/* ── Reports & Analytics ──────────────────────────────── */}
      {canSeeReports(role) && (
        <SidebarMenuItem
          to='/apps/reports-management/reports'
          msIcon='assessment'
          title={intl.formatMessage({id: 'MENU.REPORTS'})}
        />
      )}

      {/* ── Gestion hôtelière ─────────────────────────────────── */}
      {showHotelMenu && (
        <SidebarMenuItemWithSub
          to='/apps/hotel-management'
          title={intl.formatMessage({id: 'MENU.HOTELMANAGEMENT'})}
          msIcon='hotel'
        >
          {canSeeHotelsContent(role) && (
            <SidebarMenuItem
              to='/apps/hotel-management/hotels'
              title={intl.formatMessage({id: 'MENU.HOTELS'})}
              msIcon='apartment'
            />
          )}
          {canSeeHotelReservations(role) && (
            <SidebarMenuItem
              to='/apps/reservation-management/reservations'
              title={intl.formatMessage({id: 'MENU.HOTEL_RESERVATIONS'})}
              msIcon='event_available'
            />
          )}
          {canSeeContentSettings(role) && (
            <SidebarMenuItemWithSub
              to='/apps/hotel-configuration'
              title={intl.formatMessage({
                id: 'MENU.HOTEL_CONFIGURATION',
                defaultMessage: 'Configuration',
              })}
              msIcon='tune'
            >
              <SidebarMenuItem
                to='/apps/facilities-management/facilities'
                title={intl.formatMessage({id: 'MENU.HOTEL_FACILITIES'})}
                msIcon='inventory'
              />
              <SidebarMenuItem
                to='/apps/themes-management/themes'
                title={intl.formatMessage({id: 'MENU.HOTEL_THEMES'})}
                msIcon='palette'
              />
              <SidebarMenuItem
                to='/apps/options-management/options'
                title={intl.formatMessage({id: 'MENU.HOTEL_OPTIONS'})}
                msIcon='extension'
              />
            </SidebarMenuItemWithSub>
          )}
        </SidebarMenuItemWithSub>
      )}

      {/* ── Gestion des circuits ──────────────────────────────── */}
      {showTourMenu && (
        <SidebarMenuItemWithSub
          to='/apps/tours'
          title={intl.formatMessage({id: 'MENU.TOURMANAGEMENT'})}
          msIcon='explore'
        >
          {canSeeToursContent(role) && (
            <SidebarMenuItem
              to='/apps/tours'
              title={intl.formatMessage({id: 'MENU.TOURS'})}
              msIcon='travel_explore'
            />
          )}
          {canSeeTourReservations(role) && (
            <SidebarMenuItem
              to='/apps/tours/reservations'
              title={intl.formatMessage({id: 'MENU.TOUR_RESERVATIONS'})}
              msIcon='inventory_2'
            />
          )}
          {canSeeToursContent(role) && (
            <SidebarMenuItem
              to='/apps/tours/promotions'
              title={intl.formatMessage({id: 'MENU.TOUR_PROMOTIONS'})}
              msIcon='sell'
            />
          )}
          {/* Only Categories / Tour type / Person types under Tour → Configuration */}
          {canSeeTourConfigurations(role) && (
            <SidebarMenuItemWithSub
              to='/apps/tours/configurations'
              title={intl.formatMessage({
                id: 'MENU.TOUR_CONFIGURATION',
                defaultMessage: 'Configuration',
              })}
              msIcon='tune'
            >
              <SidebarMenuItem
                to='/apps/tours/configurations?tab=categories'
                title={intl.formatMessage({id: 'TOUR_CONFIGURATIONS.CATEGORIES'})}
                msIcon='category'
              />
              <SidebarMenuItem
                to='/apps/tours/configurations?tab=tourTypes'
                title={intl.formatMessage({id: 'TOUR.FIELDS.TYPE'})}
                msIcon='tour'
              />
              <SidebarMenuItem
                to='/apps/tours/configurations?tab=personTypes'
                title={intl.formatMessage({id: 'TOUR_CONFIGURATIONS.PERSON_TYPES'})}
                msIcon='groups'
              />
            </SidebarMenuItemWithSub>
          )}
        </SidebarMenuItemWithSub>
      )}

      {/* ── Mail Orders ───────────────────────────────────────── */}
      {canSeeMailOrders(role) && (
        <SidebarMenuItem
          to='/apps/mail-orders'
          msIcon='payments'
          title={intl.formatMessage({id: 'MENU.MAIL_ORDERS'})}
        />
      )}

      {/* ── Clients ───────────────────────────────────────────── */}
      {canSeeClients(role) && (
        <SidebarMenuItem
          to='/apps/clients-management/clients'
          msIcon='groups'
          title={intl.formatMessage({id: 'MENU.CLIENTS'})}
        />
      )}

      {/* ── Équipe ────────────────────────────────────────────── */}
      {canSeeUserManagement(role) && (
        <SidebarMenuItem
          to='/apps/user-management/users'
          msIcon='badge'
          title={intl.formatMessage({id: 'MENU.USERS_MANAGEMENT'})}
        />
      )}

      {/* ── Avis ──────────────────────────────────────────────── */}
      {canSeeReviews(role) && (
        <SidebarMenuItem
          to='/apps/reviews-management'
          msIcon='rate_review'
          title={intl.formatMessage({id: 'MENU.REVIEWS'})}
          badgeCount={pendingReviewsCount}
        />
      )}

      {/* ── Blog ──────────────────────────────────────────────── */}
      {canSeeBlog(role) && (
        <SidebarMenuItem
          to='/apps/blog-management'
          msIcon='article'
          title={intl.formatMessage({id: 'MENU.BLOG_MANAGEMENT'})}
        />
      )}

      {/* ── Newsletter ────────────────────────────────────────── */}
      {canSeeNewsletter(role) && (
        <SidebarMenuItem
          to='/apps/newsletter-management/newsletters'
          msIcon='mail'
          title={intl.formatMessage({id: 'MENU.NEWSLETTER'})}
        />
      )}

      {/* ── Pages ─────────────────────────────────────────────── */}
      {canSeePages(role) && (
        <SidebarMenuItem
          to='/apps/pages-management/pages'
          msIcon='pages'
          title={intl.formatMessage({id: 'MENU.PAGESMANAGEMENT'})}
        />
      )}

      {/* ── Paramètres ────────────────────────────────────────── */}
      {showSettingsMenu && (
        <SidebarMenuItemWithSub
          to='/apps/settings'
          title={intl.formatMessage({id: 'MENU.SETTINGS'})}
          msIcon='settings'
        >
          {/* Cities / Currency / Payment / Home block stay under Settings */}
          {canSeeTourConfigurations(role) && (
            <SidebarMenuItemWithSub
              to='/apps/tours/configurations'
              title={intl.formatMessage({id: 'MENU.TOUR_CONFIGURATIONS'})}
              msIcon='tune'
            >
              <SidebarMenuItem
                to='/apps/tours/configurations?tab=cities'
                title={intl.formatMessage({id: 'TOUR_CONFIGURATIONS.CITIES'})}
                msIcon='location_city'
              />
              <SidebarMenuItem
                to='/apps/tours/configurations?tab=currency'
                title={intl.formatMessage({id: 'TOUR_CONFIGURATIONS.CURRENCY'})}
                msIcon='currency_exchange'
              />
              <SidebarMenuItem
                to='/apps/tours/configurations?tab=paymentMethods'
                title={intl.formatMessage({id: 'TOUR_CONFIGURATIONS.PAYMENT_METHODS'})}
                msIcon='payments'
              />
              <SidebarMenuItem
                to='/apps/tours/configurations?tab=featured'
                title={intl.formatMessage({id: 'TOUR_CONFIGURATIONS.FEATURED'})}
                msIcon='star'
              />
              <SidebarMenuItem
                to='/apps/tours/configurations?tab=googleTag'
                title={intl.formatMessage({id: 'TOUR_CONFIGURATIONS.GOOGLE_TAG'})}
                msIcon='analytics'
              />
            </SidebarMenuItemWithSub>
          )}
          {canSeeGlobalSettings(role) && (
            <>
              <SidebarMenuItem
                to='/apps/header-management/header'
                title={intl.formatMessage({id: 'MENU.HEADERMANAGEMENT'})}
                msIcon='view_headline'
              />
              <SidebarMenuItem
                to='/apps/footer-management/footer'
                title={intl.formatMessage({id: 'MENU.FOOTERMANAGEMENT'})}
                msIcon='view_week'
              />
              <SidebarMenuItem
                to='/apps/footer-management/legal'
                title={intl.formatMessage({id: 'MENU.LEGAL_COOKIES'})}
                msIcon='policy'
              />
            </>
          )}
        </SidebarMenuItemWithSub>
      )}

      <SidebarMenuItem
        to='/crafted/account/overview'
        msIcon='account_circle'
        title={intl.formatMessage({id: 'MENU.MY_ACCOUNT'})}
      />
    </nav>
  )
}

export {SidebarMenuMain}
